import { computed, signal, WritableSignal } from '@angular/core';
import type { Map as MaplibreMap } from 'maplibre-gl';
import { keepBasemapExtrusionsHidden } from './basemap-extrusions';
import { readTrajectorySettings, TrajectorySettings, writeTrajectorySettings } from './person-motion';
import { BUILDING_LAYER_ID, BuildingLayer, BuildingPerson } from './building-layer';
import { AutoFloor, floorToShow, orbit } from './follow-camera';
import { geoToModel, levelOf, modelToGeo, roomOf } from './site-model-georef';
import { SiteModel, SiteModelLevel, SiteModelRoom } from './site-model.types';
import { keepLayerAcrossStyleChanges } from './style-reload';

/** Pente de la carte en vue 3D (degrés). */
const PITCH_3D = 58;

/** Étiquette de pièce posée sur le plancher de l'étage affiché. */
export interface RoomLabel {
  id: string;
  name: string;
  areaM2: number | null;
  lat: number;
  lon: number;
  alt: number;
}

/**
 * État du bâtiment 3D d'une carte de site (ADR-017) : modèle chargé, étage affiché, vue 2D / 3D,
 * et ce qu'il faut aux marqueurs pour se placer à leur altitude et s'estomper hors de l'étage.
 * Partagé par la carte du site et la carte de la fiche tracker.
 */
export class SiteBuildingState {
  /**
   * @param shown afficher le modèle 3D dès son chargement ; `false` : la carte garde son plan en
   *   contours GeoJSON jusqu'à {@link show} (fiche tracker, décision du 2026-10-29).
   */
  constructor(shown = true) {
    this.shown = signal(shown);
  }

  readonly model = signal<SiteModel | null>(null);
  /** Modèle 3D affiché (sinon plan en contours) ; sans effet tant qu'aucun modèle n'est chargé. */
  readonly shown: WritableSignal<boolean>;
  /** Bâtiment 3D chargé et affiché : les marqueurs se placent à leur altitude. */
  readonly active = computed(() => this.model() !== null && this.shown());
  /** Étage affiché (`id` du niveau) ou `all` : bâtiment entier, toit compris. */
  readonly floor = signal<string>('all');
  readonly is3d = signal(false);
  /** Incrémenté quand la caméra change : les décalages d'altitude des marqueurs sont à recalculer. */
  readonly frame = signal(0);
  readonly zoom = signal(0);
  readonly error = signal<string | null>(null);
  /** Réglages de démo du mouvement des personnages, mémorisés dans ce navigateur. */
  readonly trajectory = signal<TrajectorySettings>(readTrajectorySettings(browserStorage()));
  /**
   * Caméra centrée sur le personnage en direct : le glisser tourne autour de lui (la carte ne se
   * déplace plus), le zoom se fait autour de lui, et l'étage affiché suit le sien. Arrêté par le
   * bouton seulement (demande d'Eddy, 2026-10-30).
   */
  readonly follow = signal(false);
  /** Positions ignorées (impossibles à pied) depuis l'affichage du bâtiment. */
  readonly rejected = signal(0);

  readonly levels = computed<SiteModelLevel[]>(() => this.model()?.levels ?? []);
  readonly roomLabels = computed<RoomLabel[]>(() => {
    const m = this.model();
    const floor = this.floor();
    if (!m || floor === 'all') {
      return [];
    }
    const level = m.levels.find(l => l.id === floor);
    return m.rooms
      .filter(r => r.level === floor)
      .map(r => {
        const g = modelToGeo(m.georef, {
          x: (r.rect[0] + r.rect[2]) / 2,
          y: (r.rect[1] + r.rect[3]) / 2,
          z: (level?.z ?? 0) + 0.05,
        });
        return { id: r.id, name: r.name, areaM2: r.areaM2 ?? null, lat: g.lat, lon: g.lon, alt: g.alt };
      });
  });

  #layer: BuildingLayer | null = null;
  #map: MaplibreMap | null = null;
  #lastMatrix = '';
  #stopRender: (() => void) | null = null;
  #restoreExtrusions: (() => void) | null = null;
  #stopStyleFollow: (() => void) | null = null;
  #stopOrbit: (() => void) | null = null;
  #stopRejected: (() => void) | null = null;
  #followPending = false;
  readonly #autoFloor = new AutoFloor();

  /**
   * Ajoute le bâtiment à la carte ; l'étage affiché par défaut est le dernier (toit coupé).
   *
   * @param overlays couches de la carte dessinées par-dessus le bâtiment (trajet et points du rejeu)
   */
  async attach(
    map: MaplibreMap,
    model: SiteModel,
    glb: ArrayBuffer,
    personModelUrl: string | null,
    overlays: readonly string[] = []
  ): Promise<void> {
    this.detach();
    this.#map = map;
    const layer = await BuildingLayer.create(map, model, glb);
    this.#layer = layer;
    layer.setTrajectory(this.trajectory());
    layer.setVisible(this.shown());
    this.#syncExtrusions();
    // Fond de carte changé (rue ↔ satellite) : la couche est remise en haut de la pile (sous les
    // `overlays`), ou rajoutée.
    this.#stopStyleFollow = keepLayerAcrossStyleChanges(map, BUILDING_LAYER_ID, () => layer.addToMap(), overlays);
    this.model.set(model);
    const top = model.levels[model.levels.length - 1];
    this.setFloor(top ? top.id : 'all');
    this.#stopRender = layer.onRender(() => {
      const key = `${map.getZoom()}|${map.getPitch()}|${map.getBearing()}|${map.getCenter().toArray().join()}`;
      if (key !== this.#lastMatrix) {
        this.#lastMatrix = key;
        this.zoom.set(map.getZoom());
        this.frame.update(f => f + 1);
      }
      this.#followPerson(map, layer);
    });
    this.#stopOrbit = this.#orbitOnDrag(map);
    this.rejected.set(0);
    this.#stopRejected = layer.onRejected(() => this.rejected.update(n => n + 1));
    if (personModelUrl) {
      // Sans mannequin (fichier absent, réseau), les marqueurs suffisent : erreur visible en console.
      layer.loadPersonModel({ url: personModelUrl }).catch(e => console.error('[site-building] personnage', e));
    }
  }

  detach(): void {
    if (this.follow()) {
      this.setFollow(false);
    }
    this.#stopOrbit?.();
    this.#stopOrbit = null;
    this.#stopRejected?.();
    this.#stopRejected = null;
    this.#stopStyleFollow?.();
    this.#stopStyleFollow = null;
    this.#stopRender?.();
    this.#stopRender = null;
    this.#restoreExtrusions?.();
    this.#restoreExtrusions = null;
    this.#layer?.remove();
    this.#layer = null;
    this.model.set(null);
  }

  /** Bascule entre le modèle 3D (`true`) et le plan en contours GeoJSON (`false`). */
  show(on: boolean): void {
    this.shown.set(on);
    this.#layer?.setVisible(on);
    this.#syncExtrusions();
    this.frame.update(f => f + 1);
  }

  /** Bâtiments extrudés du fond de carte masqués tant que le modèle est affiché, rendus sinon. */
  #syncExtrusions(): void {
    const map = this.#map;
    if (map && this.#layer && this.shown()) {
      this.#restoreExtrusions ??= keepBasemapExtrusionsHidden(map);
    } else {
      this.#restoreExtrusions?.();
      this.#restoreExtrusions = null;
    }
  }

  setFloor(levelId: string): void {
    this.floor.set(levelId);
    this.#layer?.setFloor(levelId);
  }

  set3d(on: boolean): void {
    this.is3d.set(on);
    const map = this.#map;
    if (map) {
      map.easeTo({ pitch: on ? PITCH_3D : 0, bearing: on ? map.getBearing() - 25 : 0, duration: 700 });
    }
  }

  setTrajectory(settings: TrajectorySettings): void {
    this.trajectory.set(settings);
    this.#layer?.setTrajectory(settings);
    writeTrajectorySettings(browserStorage(), settings);
  }

  /**
   * Suivre le personnage en direct : la carte reste centrée sur lui, le glisser tourne autour, le
   * zoom se fait autour de lui, l'étage affiché devient le sien.
   */
  setFollow(on: boolean): void {
    this.follow.set(on);
    this.#autoFloor.reset();
    const map = this.#map;
    if (!map) {
      return;
    }
    // Zoom autour du centre (le personnage) : autour du pointeur, le recentrage le ferait trembler.
    const around = on ? { around: 'center' as const } : undefined;
    map.scrollZoom.disable();
    map.scrollZoom.enable(around);
    map.touchZoomRotate.disable();
    map.touchZoomRotate.enable(around);
    if (on) {
      map.dragPan.disable();
    } else {
      map.dragPan.enable();
    }
    const center = on ? this.#layer?.personLngLat() : null;
    if (center) {
      map.easeTo({ center, duration: 400 });
    }
  }

  /** En suivi, glisser (clic gauche, un doigt) tourne la caméra autour du personnage. */
  #orbitOnDrag(map: MaplibreMap): () => void {
    const el = map.getCanvasContainer();
    let last: { x: number; y: number } | null = null;
    const down = (e: PointerEvent) => {
      // Deuxième doigt : pincement (zoom), pas de rotation.
      last = this.follow() && e.isPrimary && e.button === 0 ? { x: e.clientX, y: e.clientY } : null;
    };
    const move = (e: PointerEvent) => {
      if (!last || !this.follow() || !e.isPrimary) {
        return;
      }
      const cam = orbit({ bearing: map.getBearing(), pitch: map.getPitch() }, e.clientX - last.x, e.clientY - last.y, {
        minPitch: map.getMinPitch(),
        maxPitch: map.getMaxPitch(),
      });
      last = { x: e.clientX, y: e.clientY };
      map.jumpTo(cam);
    };
    const up = () => (last = null);
    el.addEventListener('pointerdown', down);
    el.addEventListener('pointermove', move);
    el.addEventListener('pointercancel', up);
    window.addEventListener('pointerup', up);
    return () => {
      el.removeEventListener('pointerdown', down);
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointercancel', up);
      window.removeEventListener('pointerup', up);
    };
  }

  /** Recentre après l'image en cours (pas pendant le rendu de la carte), une fois par image. */
  #followPerson(map: MaplibreMap, layer: BuildingLayer): void {
    if (!this.follow()) {
      return;
    }
    const feet = layer.personFeetY();
    const model = this.model();
    if (feet !== null && model) {
      const floor = this.#autoFloor.next(floorToShow(model.levels, feet)?.id ?? null);
      if (floor) {
        this.setFloor(floor);
      }
    }
    if (this.#followPending) {
      return;
    }
    const center = layer.personLngLat();
    if (!center) {
      return;
    }
    const c = map.getCenter();
    if (Math.abs(c.lng - center[0]) < 1e-7 && Math.abs(c.lat - center[1]) < 1e-7) {
      return;
    }
    this.#followPending = true;
    requestAnimationFrame(() => {
      this.#followPending = false;
      if (this.follow() && !map.isMoving()) {
        map.setCenter(center);
      }
    });
  }

  setPeople(people: BuildingPerson[]): void {
    this.#layer?.setPeople(people);
  }

  /** Décalage écran d'un marqueur posé au sol pour le montrer à son altitude ; `null` sans bâtiment. */
  offset(
    lat: number | null | undefined,
    lon: number | null | undefined,
    alt: number | null | undefined
  ): [number, number] | null {
    this.frame();
    if (!this.#layer || !this.shown() || lat == null || lon == null || alt == null) {
      return null;
    }
    return this.#layer.altitudeOffset(lat, lon, alt);
  }

  /** Étage d'une position (`null` sans modèle ou sans altitude). */
  levelAt(
    lat: number | null | undefined,
    lon: number | null | undefined,
    alt: number | null | undefined
  ): SiteModelLevel | null {
    const m = this.model();
    if (!m || lat == null || lon == null || alt == null) {
      return null;
    }
    return levelOf(m.levels, geoToModel(m.georef, { lat, lon, alt }).z);
  }

  /** Pièce d'une position (`null` hors pièce, sans modèle ou sans altitude). */
  roomAt(
    lat: number | null | undefined,
    lon: number | null | undefined,
    alt: number | null | undefined
  ): SiteModelRoom | null {
    const m = this.model();
    if (!m || lat == null || lon == null || alt == null) {
      return null;
    }
    return roomOf(m.levels, m.rooms, geoToModel(m.georef, { lat, lon, alt })).room;
  }

  /** Vrai si la position est à un autre étage que celui affiché (pour estomper son marqueur). */
  isOtherFloor(
    lat: number | null | undefined,
    lon: number | null | undefined,
    alt: number | null | undefined
  ): boolean {
    if (!this.shown()) {
      return false;
    }
    const floor = this.floor();
    const level = this.levelAt(lat, lon, alt);
    return floor !== 'all' && !!level && level.id !== floor;
  }
}

/** `localStorage` quand il existe (navigateur), sinon un stockage vide (tests, rendu serveur). */
function browserStorage(): Pick<Storage, 'getItem' | 'setItem'> {
  try {
    return globalThis.localStorage ?? { getItem: () => null, setItem: () => undefined };
  } catch {
    return { getItem: () => null, setItem: () => undefined };
  }
}
